import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import MessageList from './components/MessageList';
import MessageInput from './components/MessageInput';
import ModeControls from './components/ModeControls';
import { Client, PollinationsAI, DeepInfra, Together, Puter, HuggingFace } from '@gpt4free/g4f.dev';

// G4F client
let GPT4FreeClient = null;

// Initialize G4F client
const initGPT4Free = async () => {
  if (GPT4FreeClient) return GPT4FreeClient;

  try {
    // Dynamic import of GPT4Free client
    //const module = await import('https://g4f.dev/dist/js/client.js');
    GPT4FreeClient = new PollinationsAI();
    console.log('GPT4Free client initialized');
    return GPT4FreeClient;
  } catch (error) {
    console.error('Failed to initialize GPT4Free client:', error);
    return null;
  }
};

// Fetch G4F models (will be defined inside component)
const fetchGPT4FreeModels = async (setGpt4freeModelsLoading, setGpt4freeModels, setSelectedGpt4freeModel, selectedGpt4freeModel) => {
  try {
    console.log('Starting to fetch G4F models...');
    setGpt4freeModelsLoading(true);

    const client = await initGPT4Free();
    console.log('G4F client initialized:', !!client);

    if (client) {
      console.log('Client methods available:', Object.keys(client));
      console.log('Models methods available:', client.models ? Object.keys(client.models) : 'No models');

      if (client.models && client.models.list) {
        console.log('Calling models.list()...');
        const modelsList = await client.models.list();
        console.log('G4F models fetched:', modelsList);

        if (Array.isArray(modelsList) && modelsList.length > 0) {
          // Convert to our format
          const formattedModels = modelsList.map(model => ({
            id: model.id,
            name: model.id,
            type: model.type,
            description: `${model.type} model`
          }));

          console.log('Formatted models:', formattedModels);

          // Ensure we have audio models by adding fallback audio models if missing
          const hasAudioModels = formattedModels.some(model => model.type === 'audio');
          if (!hasAudioModels) {
            const fallbackAudioModels = [
              { id: 'openai-audio', name: 'OpenAI Audio', type: 'audio', description: 'OpenAI audio generation model' },
              { id: 'gpt-4o-audio', name: 'GPT-4o Audio', type: 'audio', description: 'GPT-4o audio generation model' },
              { id: 'gpt-4o-mini-tts', name: 'GPT-4o Mini TTS', type: 'audio', description: 'G4F Mini text-to-speech model' }
            ];
            formattedModels.push(...fallbackAudioModels);
            console.log('Added fallback audio models:', fallbackAudioModels);
          }

          setGpt4freeModels(formattedModels);

          // Set default model if not set
          if (formattedModels.length > 0 && !selectedGpt4freeModel) {
            setSelectedGpt4freeModel(formattedModels[0].id);
            console.log('Set default G4F model:', formattedModels[0].id);
          }
        } else {
          console.warn('Models list is empty or not an array');
          throw new Error('No models available');
        }
      } else {
        console.warn('Models.list method not available');
        throw new Error('Models API not available');
      }
    } else {
      console.warn('GPT4Free client not initialized');
      throw new Error('Client initialization failed');
    }
  } catch (error) {
    console.error('Failed to fetch G4F models:', error);
    console.log('Using fallback models...');

    // Fallback models
    const fallbackModels = [
      { id: 'gpt-4.1', name: 'GPT-4.1', type: 'chat', description: 'GPT-4.1 model' },
      { id: 'gpt-4o', name: 'GPT-4o', type: 'chat', description: 'GPT-4o model' },
      { id: 'deepseek-v3', name: 'DeepSeek-V3', type: 'chat', description: 'DeepSeek model' },
      { id: 'flux', name: 'Flux', type: 'image', description: 'Image generation model' },
      { id: 'dalle-3', name: 'DALL-E 3', type: 'image', description: 'Image generation model' },
      { id: 'openai-audio', name: 'OpenAI Audio', type: 'audio', description: 'OpenAI audio generation model' },
      { id: 'gpt-4o-mini-tts', name: 'G4F Mini TTS', type: 'audio', description: 'G4F Mini text-to-speech model' }
    ];

    console.log('Setting fallback models:', fallbackModels);
    setGpt4freeModels(fallbackModels);

    // Set default fallback model
    if (!selectedGpt4freeModel) {
      setSelectedGpt4freeModel(fallbackModels[0].id);
      console.log('Set fallback default model:', fallbackModels[0].id);
    }
  } finally {
    console.log('Finished fetching G4F models');
    setGpt4freeModelsLoading(false);
  }
};

function App() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [puterLoaded, setPuterLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [modelSearchTerm, setModelSearchTerm] = useState('');
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const modelDropdownRef = useRef(null);
  const [mode, setMode] = useState('gpt4free-chat'); // 'chat', 'image', 'img2txt', 'txt2speech', 'gpt4free-chat', 'gpt4free-image', 'gpt4free-audio', 'gpt4free-tts'

  // Text-to-speech settings
  const [ttsLanguage, setTtsLanguage] = useState('en-US');
  const [ttsVoice, setTtsVoice] = useState('Joanna');
  const [ttsEngine, setTtsEngine] = useState('standard');

  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);

  // G4F models
  const [gpt4freeModels, setGpt4freeModels] = useState([]);
  const [selectedGpt4freeModel, setSelectedGpt4freeModel] = useState('gpt-4.1');
  const [gpt4freeModelsLoading, setGpt4freeModelsLoading] = useState(false);

  // Filter models based on search term
  const filteredModels = models.filter(model =>
    model.name && model.id &&
    (model.name.toLowerCase().includes(modelSearchTerm.toLowerCase()) ||
     model.id.toLowerCase().includes(modelSearchTerm.toLowerCase()))
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Reset textarea height when input is cleared
    if (inputValue === '' && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [inputValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target)) {
        setIsModelDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  useEffect(() => {
    // Fetch chat models from local JSON file
    setModelsLoading(true);
    console.log('Fetching models from /models.json');

    fetch('/models.json?v=' + Date.now()) // Add cache busting
      .then(res => {
        console.log('Fetch response status:', res.status);
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('Fetched data:', data);
        const modelsList = Array.isArray(data.models) ? data.models : [];
        console.log('Models list:', modelsList.length, 'models');

        // Convert string array to objects with id and name
        const modelsObjects = modelsList.map(modelId => ({
          id: modelId,
          name: modelId.replace(/^openrouter:/, '').replace(/:/g, ' - ').replace(/\//g, ' / '),
          description: `AI model: ${modelId}`
        }));

        console.log('Processed models objects:', modelsObjects.length);
        setModels(modelsObjects);
        if (modelsObjects.length > 0) setSelectedModel(modelsObjects[0].id);
        setModelsLoading(false);
      })
      .catch((error) => {
        console.error('Error loading models:', error);
        // Fallback models if JSON file fails to load
        const fallbackModels = [
          { id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Fast and efficient model' },
          { id: 'gpt-4o', name: 'GPT-4o', description: 'Most capable GPT-4o model' }
        ];
        console.log('Using fallback models:', fallbackModels.length);
        setModels(fallbackModels);
        setSelectedModel(fallbackModels[0].id);
        setModelsLoading(false);
      });
  }, []);

  useEffect(() => {
    // Check if puter is loaded
    if (window.puter) {
      setPuterLoaded(true);
    } else {
      // Listen for script load
      const interval = setInterval(() => {
        if (window.puter) {
          setPuterLoaded(true);
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, []);

  // Fetch G4F models when component mounts
  useEffect(() => {
    fetchGPT4FreeModels(setGpt4freeModelsLoading, setGpt4freeModels, setSelectedGpt4freeModel, selectedGpt4freeModel);
  }, []);

  // Auto-select first model when switching to G4F modes
  useEffect(() => {
    if ((mode === 'gpt4free-chat' || mode === 'gpt4free-image' || mode === 'gpt4free-audio') && gpt4freeModels.length > 0) {
      // Only auto-select if no valid model is currently selected
      const currentModel = gpt4freeModels.find(m => m.id === selectedGpt4freeModel);
      const isCurrentModelValid = currentModel && (
        (mode === 'gpt4free-audio' && currentModel.type === 'audio') ||
        (mode === 'gpt4free-chat' && currentModel.type === 'chat') ||
        (mode === 'gpt4free-image' && currentModel.type === 'image') ||
        (mode === 'gpt4free-tts' && currentModel.type === 'audio')  // TTS also uses audio models
      );

      if (!isCurrentModelValid) {
        if (mode === 'gpt4free-audio' || mode === 'gpt4free-tts') {
          // For both audio modes (G4F Modality and TTS), prefer to keep existing audio model
          const audioModels = gpt4freeModels.filter(model => model.type === 'audio');
          if (audioModels.length > 0) {
            // If current model is audio type, keep it; otherwise default to openai-audio
            if (currentModel && currentModel.type === 'audio') {
              console.log(`Keeping existing audio model: ${selectedGpt4freeModel} for ${mode} mode`);
              // Model is already valid, no change needed
            } else {
              console.log(`Setting ${mode} mode to use openai-audio`);
              setSelectedGpt4freeModel('openai-audio');
            }
          }
        } else {
          const modelType = mode === 'gpt4free-chat' ? 'chat' : 'image';
          const filteredModels = gpt4freeModels.filter(model => model.type === modelType);

          if (filteredModels.length > 0) {
            const firstModel = filteredModels[0];
            console.log(`Auto-selecting first ${modelType} model:`, firstModel.id);
            setSelectedGpt4freeModel(firstModel.id);
          }
        }
      }
    }
  }, [mode, gpt4freeModels, selectedGpt4freeModel]);

  const handleFileSelect = (e) => {
    const fileList = e.target.files;
    console.log('File input change event:', fileList);

    if (!fileList || fileList.length === 0) {
      console.log('No files selected');
      setFiles([]);
      return;
    }

    const selectedFiles = Array.from(fileList);
    console.log('Files selected:', selectedFiles.length);
    selectedFiles.forEach((file, index) => {
      console.log(`File ${index + 1}:`, file.name, file.type, file.size, 'bytes');
    });
    setFiles(selectedFiles);
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() === '' && files.length === 0 || isLoading) return;

    const userInput = inputValue.trim();

    // Add user message to chat (without attachment info for now)
    const userMessage = { id: Date.now(), text: userInput, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Clear files immediately - this will update the UI right away
    console.log('Clearing files immediately after send button clicked');
    console.log('Files before clearing:', files.length);
    setFiles([]);

    // Clear the file input field
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
      console.log('File input field cleared');
    }

    // Process files to data URLs (using a copy of the files array since it's now cleared)
    const fileUrls = [];
    const filesCopy = [...files]; // Make a copy before processing
    const processedFiles = []; // Store processed file info with data URLs
    console.log('Processing files copy:', filesCopy.length);

    if (filesCopy && Array.isArray(filesCopy) && filesCopy.length > 0) {
      for (let i = 0; i < filesCopy.length; i++) {
        const file = filesCopy[i];
        console.log('Processing file:', file.name, file.type, file.size);
        try {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              console.log('File loaded successfully:', file.name);
              resolve(e.target.result);
            };
            reader.onerror = (e) => {
              console.error('Error reading file:', file.name, e);
              reject(e);
            };
            reader.readAsDataURL(file);
          });

          const processedFile = {
            name: file.name,
            type: file.type,
            dataUrl: dataUrl
          };

          processedFiles.push(processedFile);

          if (file.type && file.type.startsWith('image/')) {
            console.log('Added image file for:', file.name);
          } else {
            fileUrls.push(processedFile);
            console.log('Added document file for:', file.name);
          }
        } catch (error) {
          console.error('Failed to process file:', file.name, error);
        }
      }
      console.log('Total files processed:', processedFiles.length);
      console.log('Total file URLs processed:', fileUrls.length);

      // Update user message with attachment info and image data
      if (filesCopy && filesCopy.length > 0) {
        const imageFiles = processedFiles.filter(f => f.type.startsWith('image/'));
        const documentFiles = processedFiles.filter(f => !f.type.startsWith('image/'));

        let attachmentInfo = '';
        const uploadedImages = [];

        if (imageFiles.length > 0) {
          const imageList = imageFiles.map(f => `🖼️ ${f.name} (Image)`).join('\n');
          attachmentInfo += `${imageList}`;
          // Store image data URLs for display
          imageFiles.forEach(f => {
            uploadedImages.push({
              name: f.name,
              dataUrl: f.dataUrl
            });
            console.log('Added image for display:', f.name, f.dataUrl.substring(0, 50) + '...');
          });
          console.log('Total uploaded images:', uploadedImages.length);
        }
        if (documentFiles.length > 0) {
          if (attachmentInfo) attachmentInfo += '\n\n';
          const documentList = documentFiles.map(f => `📄 ${f.name} (Document)`).join('\n');
          attachmentInfo += `${documentList}`;
        }

        const updatedMessage = {
          ...userMessage,
          text: userInput + (attachmentInfo ? `\n\n📎 Attachments:\n${attachmentInfo}` : ''),
          uploadedImages: uploadedImages
        };
        setMessages(prev => prev.map(msg => msg.id === userMessage.id ? updatedMessage : msg));
      }
    } else {
      console.log('No files to process or files is not an array');
    }

    try {
      if (mode === 'image') {
        // Handle image generation
        await handleImageGeneration(userInput);
      } else if (mode === 'img2txt') {
        // Handle image-to-text
        await handleImageToText(userInput);
      } else if (mode === 'txt2speech') {
        // Handle text-to-speech
        await handleTextToSpeech(userInput);
      } else if (mode === 'gpt4free-chat') {
        // Handle GPT4Free chat
        await handleGPT4FreeChat(userInput);
      } else if (mode === 'gpt4free-image') {
        // Handle GPT4Free image generation
        await handleGPT4FreeImage(userInput);
      } else if (mode === 'gpt4free-audio') {
        // Handle GPT4Free audio generation
        await handleGPT4FreeAudio(userInput);
      } else if (mode === 'gpt4free-tts') {
        // Handle GPT4Free TTS generation
        await handleGPT4FreeTTS(userInput);
      } else if (puterLoaded && window.puter && window.puter.ai && window.puter.ai.chat) {
        // Streaming response with puter.js
        try {
          let chatResp;
          const imageFiles = processedFiles.filter(f => f.type.startsWith('image/'));
          const documentFiles = processedFiles.filter(f => !f.type.startsWith('image/'));
          const hasImages = imageFiles.length > 0;
          const hasDocuments = documentFiles.length > 0;
          const modelToUse = getModelForRequest(hasImages, hasDocuments);

          console.log('Calling Puter.js chat API with:', {
            userInput: userInput ? userInput.substring(0, 50) + '...' : 'empty',
            imageCount: imageFiles.length,
            documentCount: documentFiles.length,
            selectedModel: selectedModel,
            modelToUse: modelToUse,
            usingFallback: modelToUse !== selectedModel
          });

          // Inform user about model selection for transparency
          if (modelToUse !== selectedModel) {
            let modelNotification = '';
            if (modelToUse === 'claude-sonnet-4') {
              modelNotification = `🔄 Using Claude-Sonnet-4 for optimal document analysis`;
            } else if (modelToUse === 'gpt-5') {
              modelNotification = `🔄 Using GPT-5 for enhanced image processing`;
            }

            if (modelNotification) {
              const notificationMessage = {
                id: Date.now() + 2,
                text: modelNotification,
                sender: 'system'
              };
              setMessages(prev => [...prev, notificationMessage]);
            }
          }

          // Prepare enhanced prompt with file information
          let enhancedPrompt = userInput;
          if (documentFiles.length > 0) {
            enhancedPrompt += '\n\nAttached files:\n' + documentFiles.map(f => `- ${f.name} (${f.type})`).join('\n');
            console.log('Enhanced prompt with file info:', enhancedPrompt.substring(0, 100) + '...');
          }

          if (imageFiles.length === 1) {
            // Single image
            console.log('Single image mode, calling puter.ai.chat with image URL');
            chatResp = await window.puter.ai.chat(enhancedPrompt, imageFiles[0].dataUrl, false, { model: modelToUse, stream: true });
          } else if (imageFiles.length > 1) {
            // Multiple images
            console.log('Multiple images mode, calling puter.ai.chat with image array');
            const imageDataUrls = imageFiles.map(f => f.dataUrl);
            chatResp = await window.puter.ai.chat(enhancedPrompt, imageDataUrls, false, { model: modelToUse, stream: true });
          } else {
            // Text only (with file info if any)
            console.log('Text only mode, calling puter.ai.chat without images');
            chatResp = await window.puter.ai.chat(enhancedPrompt, { model: modelToUse, stream: true });
          }
          console.log('Puter.js API call successful');

          const botId = Date.now() + 1;
          let accumulatedText = '';

          // Add initial empty bot message for streaming
          setMessages(prev => [...prev, { id: botId, text: '', sender: 'bot' }]);

          const timeout = setTimeout(() => {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === botId ? { ...msg, text: accumulatedText || 'Response timed out after 30 seconds.' } : msg
              )
            );
            setIsLoading(false);
          }, 30000); // 30s timeout

          try {
            for await (const part of chatResp) {
              const newText = part?.text ?? '';
              accumulatedText += newText;

              // Update the bot message with accumulated text
              setMessages(prev =>
                prev.map(msg =>
                  msg.id === botId ? { ...msg, text: accumulatedText } : msg
                )
              );
            }
            clearTimeout(timeout);
          } catch (streamErr) {
            clearTimeout(timeout);
            const errorText = accumulatedText || `Streaming error: ${streamErr?.message || streamErr}`;
            setMessages(prev =>
              prev.map(msg =>
                msg.id === botId ? { ...msg, text: errorText } : msg
              )
            );
          }
        } catch (err) {
          // If streaming fails, add error message
          console.error('Puter.js chat API error:', err);
          const errorDetails = {
            message: err?.message,
            name: err?.name,
            stack: err?.stack,
            fullError: typeof err === 'object' ? JSON.stringify(err, null, 2) : String(err)
          };
          console.error('Error details:', errorDetails);

          const botMessage = {
            id: Date.now() + 1,
            text: `Chat API Error: ${errorDetails.message || errorDetails.fullError}\n\nPlease check the browser console for more details.`,
            sender: 'bot'
          };
          setMessages(prev => [...prev, botMessage]);
        }
      } else {
        // Fallback when puter.js is not available
        const botMessage = {
          id: Date.now() + 1,
          text: `Puter.js is not loaded yet or chat API unavailable. Please wait and try again.\n\nSimulated response from ${selectedModel}: "Thank you for your message: '${userInput}'. This is a simulated response since the API is not available."`,
          sender: 'bot'
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      // General error handling
      const errorMessage = {
        id: Date.now() + 1,
        text: `Unexpected error: ${error.message}`,
        sender: 'bot'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGPT4FreeChat = async (text) => {
    console.log('=== handleGPT4FreeChat called ===');
    console.log('Text received:', text);

    try {
      const client = await initGPT4Free();
      if (!client) {
        const errorMessage = {
          id: Date.now() + 1,
          text: `G4F client not available. Please check your internet connection and try again.`,
          sender: 'bot'
        };
        setMessages(prev => [...prev, errorMessage]);
        setIsLoading(false);
        return;
      }

      // Add system message about using G4F
      const notificationMessage = {
        id: Date.now() + 2,
        text: `🔄 Using G4F for chat completion`,
        sender: 'system'
      };
      setMessages(prev => [...prev, notificationMessage]);

      // Use streaming completion with selected model
      const stream = await client.chat.completions.create({
        model: selectedGpt4freeModel,
        messages: [{ role: 'user', content: text }],
        stream: true
      });

      const botId = Date.now() + 1;
      let accumulatedText = '';

      // Add initial empty bot message for streaming
      setMessages(prev => [...prev, { id: botId, text: '', sender: 'bot' }]);

      try {
        for await (const chunk of stream) {
          const newText = chunk.choices[0]?.delta?.content || '';
          accumulatedText += newText;

          // Update the bot message with accumulated text
          setMessages(prev =>
            prev.map(msg =>
              msg.id === botId ? { ...msg, text: accumulatedText } : msg
            )
          );
        }
      } catch (streamErr) {
        console.error('G4F streaming error:', streamErr);
        const errorText = accumulatedText || `Streaming error: ${streamErr?.message || streamErr}`;
        setMessages(prev =>
          prev.map(msg =>
            msg.id === botId ? { ...msg, text: errorText } : msg
          )
        );
      }
    } catch (error) {
      console.error('G4F chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: `G4F chat failed: ${error.message}\n\nPlease check your internet connection and try again.`,
        sender: 'bot'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGPT4FreeImage = async (prompt) => {
    console.log('=== handleGPT4FreeImage called ===');
    console.log('Prompt received:', prompt);

    try {
      const client = await initGPT4Free();
      if (!client) {
        const errorMessage = {
          id: Date.now() + 1,
          text: `G4F client not available. Please check your internet connection and try again.`,
          sender: 'bot'
        };
        setMessages(prev => [...prev, errorMessage]);
        setIsLoading(false);
        return;
      }

      // Add system message about using G4F
      const notificationMessage = {
        id: Date.now() + 2,
        text: `🔄 Using G4F for image generation`,
        sender: 'system'
      };
      setMessages(prev => [...prev, notificationMessage]);

      // Generate image using GPT4Free with selected model
      const response = await client.images.generate({
        model: selectedGpt4freeModel,
        prompt: prompt
      });

      const imageUrl = response.data && response.data[0] ? response.data[0].url : response.data.url;
      console.log('Generated image URL:', imageUrl);

      // Create a bot message with the generated image
      const botMessage = {
        id: Date.now() + 1,
        text: `Generated image for: "${prompt}"`,
        sender: 'bot',
        type: 'image',
        imageElement: null,
        gpt4freeImageUrl: imageUrl
      };
      setMessages(prev => [...prev, botMessage]);

    } catch (error) {
      console.error('G4F image generation error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: `Image generation failed: ${error.message}\n\nPlease check your internet connection and try again.`,
        sender: 'bot'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGPT4FreeAudio = async (text) => {
    console.log('=== handleGPT4FreeAudio called ===');
    console.log('Text received:', text);

    try {
      const client = await initGPT4Free();
      if (!client) {
        const errorMessage = {
          id: Date.now() + 1,
          text: `G4F client not available. Please check your internet connection and try again.`,
          sender: 'bot'
        };
        setMessages(prev => [...prev, errorMessage]);
        setIsLoading(false);
        return;
      }

      // Add system message about using G4F
      const notificationMessage = {
        id: Date.now() + 2,
        text: `🔄 Using G4F for audio generation`,
        sender: 'system'
      };
      setMessages(prev => [...prev, notificationMessage]);

      // Generate audio using GPT4Free with audio modalities
      const result = await client.chat.completions.create({
        model: selectedGpt4freeModel,
        messages: [{ role: 'user', content: text }],
        audio: {
          voice: 'alloy', // Default voice
          format: 'mp3'
        },
        modalities: ['text', 'audio']
      });

      console.log('G4F audio result:', result);

      // Check if audio was generated in the response
      let audioData = null;

      // Try different response structures
      if (result.choices && result.choices[0] && result.choices[0].message) {
        const message = result.choices[0].message;
        // Check if message has audio data
        if (message.audio) {
          audioData = message.audio.data || message.audio;
        }
        // If no audio but we have text, use text response
        else if (message.content) {
          const botMessage = {
            id: Date.now() + 1,
            text: message.content,
            sender: 'bot'
          };
          setMessages(prev => [...prev, botMessage]);
          setIsLoading(false);
          return;
        }
      }
      // Check top-level audio property
      else if (result.audio) {
        audioData = result.audio.data || result.audio;
      }

      if (audioData) {
        console.log('Audio data received:', audioData);

        // Convert audio data to blob if it's base64
        let audioBlob = audioData;
        if (typeof audioData === 'string') {
          // Assume it's base64 encoded
          try {
            const binaryString = atob(audioData);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            audioBlob = new Blob([bytes], { type: 'audio/mpeg' });
          } catch (error) {
            console.error('Failed to decode base64 audio:', error);
            audioBlob = audioData;
          }
        }

        // Create a bot message with the audio
        const botMessage = {
          id: Date.now() + 1,
          text: `🎵 Audio generated for: "${text.substring(0, 100)}${text.length > 100 ? '...' : ''}"`,
          sender: 'bot',
          type: 'audio',
          audioStream: audioBlob,
          audioFileName: 'alloy.mp3'
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        // Fallback response if no audio data found
        const botMessage = {
          id: Date.now() + 1,
          text: `Audio generation completed, but no audio data was received. Response: ${JSON.stringify(result)}`,
          sender: 'bot'
        };
        setMessages(prev => [...prev, botMessage]);
      }

    } catch (error) {
      console.error('G4F audio generation error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: `Audio generation failed: ${error.message}\n\nPlease check your internet connection and try again.`,
        sender: 'bot'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGPT4FreeTTS = async (text) => {
    console.log('=== handleGPT4FreeTTS called ===');
    console.log('Text received:', text);

    try {
      // Check if Web Speech API is available
      if (!('speechSynthesis' in window)) {
        throw new Error('Web Speech API is not supported in this browser');
      }

      // Add system message about using Web Speech API
      const notificationMessage = {
        id: Date.now() + 2,
        text: `🔄 Using Browser Web Speech API for TTS`,
        sender: 'system'
      };
      setMessages(prev => [...prev, notificationMessage]);

      // Create speech utterance
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US'; // Set language
      utterance.rate = 1.0; // Speed (0.1 to 10)
      utterance.pitch = 1.0; // Pitch (0 to 2)
      utterance.volume = 1.0; // Volume (0 to 1)

      // Get available voices and select a good English voice
      const voices = speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice =>
        voice.lang.startsWith('en') &&
        (voice.name.includes('Google') || voice.name.includes('English') || voice.localService)
      ) || voices.find(voice => voice.lang.startsWith('en'));

      if (preferredVoice) {
        utterance.voice = preferredVoice;
        console.log('Selected voice:', preferredVoice.name, preferredVoice.lang);
      }

      // Create audio blob by recording the speech (advanced approach)
      let audioBlob = null;
      try {
        // Create a MediaStream from speech synthesis
        const audioContext = new AudioContext();
        const streamDestination = audioContext.createMediaStreamDestination();

        // For simplicity, we'll use the Web Speech API directly
        // and create a placeholder audio element that indicates speech is playing
        utterance.onstart = () => {
          console.log('Speech synthesis started');
        };

        utterance.onend = () => {
          console.log('Speech synthesis completed');
          setIsLoading(false);
        };

        utterance.onerror = (event) => {
          console.error('Speech synthesis error:', event.error);
          throw new Error(`Speech synthesis failed: ${event.error}`);
        };

        // Create a simple audio blob placeholder (1 second of silence)
        // In a real implementation, you might record the actual speech
        const silence = new Uint8Array(44100 * 2); // 1 second of silence at 44.1kHz
        audioBlob = new Blob([silence], { type: 'audio/wav' });

      } catch (audioError) {
        console.log('Audio recording not available, using placeholder:', audioError.message);
        // Create a minimal placeholder audio
        const silence = new Uint8Array(1000);
        audioBlob = new Blob([silence], { type: 'audio/wav' });
      }

      // Start speech synthesis
      speechSynthesis.speak(utterance);

      // Create a bot message indicating TTS is active
      const botMessage = {
        id: Date.now() + 1,
        text: `🎵 Web Speech API TTS Active: "${text.substring(0, 100)}${text.length > 100 ? '...' : ''}"\nVoice: ${preferredVoice?.name || 'Browser Default'}\nLanguage: ${utterance.lang}\nStatus: Speaking...`,
        sender: 'bot',
        type: 'audio',
        audioStream: audioBlob,
        audioFileName: `web-speech-${Date.now()}.wav`
      };
      setMessages(prev => [...prev, botMessage]);

      console.log('Web Speech TTS initiated');

    } catch (error) {
      console.error('Web Speech TTS error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: `Web Speech TTS failed: ${error.message}\n\nTry using a different browser or check if speech synthesis is enabled.`,
        sender: 'bot'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      // Note: We don't set loading to false here because speech might still be playing
      // The utterance.onend handler will handle this
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  };

  const handleModelSelect = (modelId) => {
    setSelectedModel(modelId);
    setIsModelDropdownOpen(false);
    setModelSearchTerm('');
  };

  const handleModelSearchChange = (e) => {
    setModelSearchTerm(e.target.value);
  };

  const toggleModelDropdown = () => {
    setIsModelDropdownOpen(!isModelDropdownOpen);
    setModelSearchTerm('');
  };

  const getSelectedModelName = () => {
    if (modelsLoading) return 'Loading models...';
    if (models.length === 0) return 'No models available';
    const selectedModelObj = models.find(model => model.id === selectedModel);
    return selectedModelObj ? selectedModelObj.name : selectedModel;
  };

  // Determine the best model for the current context
  const getModelForRequest = (hasImages, hasDocuments) => {
    // For chat mode with documents, prefer claude-sonnet-4 for better document analysis
    if (mode === 'chat' && hasDocuments && models.some(model => model.id === 'claude-sonnet-4')) {
      console.log('Using claude-sonnet-4 for document analysis in chat mode');
      return 'claude-sonnet-4';
    }
    // For chat mode with images, prefer gpt-5 for better vision capabilities
    if (mode === 'chat' && hasImages && models.some(model => model.id === 'gpt-5')) {
      console.log('Using gpt-5 as fallback for image upload in chat mode');
      return 'gpt-5';
    }
    // Otherwise use the selected model
    return selectedModel;
  };



  const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    try {
      const urlObj = new URL(url);
      // Check if it's http or https
      if (!['http:', 'https:'].includes(urlObj.protocol)) return false;

      // Check for common image extensions or known image hosting domains
      const hasImageExtension = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i.test(url);
      const isImageHost = /\b(cdn\.|imgur|unsplash|pixabay|pexels|flickr|cloudinary|amazonaws\.com|googleusercontent\.com)\b/i.test(url);

      return hasImageExtension || isImageHost;
    } catch {
      return false;
    }
  };

  const handleImageGeneration = async (prompt) => {
    try {
      if (puterLoaded && window.puter && window.puter.ai && window.puter.ai.txt2img) {
        // Generate image using puter.js txt2img
        const image = await window.puter.ai.txt2img(prompt, false); // Set to false for production, true for testing

        // Create a bot message with the generated image
        const botMessage = {
          id: Date.now() + 1,
          text: `Generated image for: "${prompt}"`,
          sender: 'bot',
          type: 'image',
          imageElement: image
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        // Fallback when puter.js is not available
        const botMessage = {
          id: Date.now() + 1,
          text: `Image generation not available. Puter.js txt2img API is not loaded.\n\nPrompt was: "${prompt}"`,
          sender: 'bot'
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      // Error handling for image generation
      const errorMessage = {
        id: Date.now() + 1,
        text: `Image generation failed: ${error.message}\n\nPrompt was: "${prompt}"`,
        sender: 'bot'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextToSpeech = async (text) => {
    console.log('=== handleTextToSpeech called ===');
    console.log('Text received:', text);
    console.log('TTS Settings:', { language: ttsLanguage, voice: ttsVoice, engine: ttsEngine });

    try {
      if (puterLoaded && window.puter && window.puter.ai && window.puter.ai.txt2speech) {
        // Validate text length (max 3000 characters)
        if (!text || text.trim() === '') {
          const errorMessage = {
            id: Date.now() + 1,
            text: `Please provide text to convert to speech.`,
            sender: 'bot'
          };
          setMessages(prev => [...prev, errorMessage]);
          setIsLoading(false);
          return;
        }

        const cleanText = text.trim();
        if (cleanText.length > 3000) {
          const errorMessage = {
            id: Date.now() + 1,
            text: `Text is too long (${cleanText.length} characters). Maximum allowed is 3000 characters.`,
            sender: 'bot'
          };
          setMessages(prev => [...prev, errorMessage]);
          setIsLoading(false);
          return;
        }

        console.log('🎯 Converting text to speech...');
        console.log('Text length:', cleanText.length);

        // Convert text to speech using puter.js txt2speech
        const audioStream = await window.puter.ai.txt2speech(cleanText, {
          language: ttsLanguage,
          voice: ttsVoice,
          engine: ttsEngine
        });

        console.log('✅ Received audio stream:', typeof audioStream, audioStream);
        console.log('Audio stream properties:', {
          constructor: audioStream?.constructor?.name,
          isBlob: audioStream instanceof Blob,
          isReadableStream: audioStream instanceof ReadableStream,
          hasArrayBuffer: typeof audioStream?.arrayBuffer === 'function',
          keys: audioStream ? Object.keys(audioStream) : 'null'
        });

        // Create a bot message with the audio
        const botMessage = {
          id: Date.now() + 1,
          text: `Text-to-speech conversion completed!\n\nText: "${cleanText.substring(0, 100)}${cleanText.length > 100 ? '...' : ''}"\nLanguage: ${ttsLanguage}\nVoice: ${ttsVoice}\nEngine: ${ttsEngine}`,
          sender: 'bot',
          type: 'audio',
          audioStream: audioStream
        };
        setMessages(prev => [...prev, botMessage]);

      } else {
        // Fallback when puter.js is not available
        console.log('❌ Puter.js txt2speech not available');
        const botMessage = {
          id: Date.now() + 1,
          text: `Text-to-speech not available. Puter.js txt2speech API is not loaded.\n\nPuter loaded: ${puterLoaded}\nWindow.puter: ${!!window.puter}\nWindow.puter.ai: ${!!(window.puter && window.puter.ai)}\nWindow.puter.ai.txt2speech: ${!!(window.puter && window.puter.ai && window.puter.ai.txt2speech)}`,
          sender: 'bot'
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      // Error handling for text-to-speech
      console.error('❌ Text-to-speech error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: `Text-to-speech failed: ${error.message}\n\nText was: "${text.substring(0, 100)}${text.length > 100 ? '...' : ''}"`,
        sender: 'bot'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      console.log('Setting loading to false');
      setIsLoading(false);
    }
  };

  const handleImageToText = async (input) => {
    console.log('=== handleImageToText called ===');
    console.log('Input received:', input);
    console.log('Input value:', inputValue);

    try {
      if (puterLoaded && window.puter && window.puter.ai && window.puter.ai.img2txt) {
        let imageSource = null;
        let displayText = '';

        // Check if user has uploaded files first
        if (files.length > 0) {
          const imageFiles = files.filter(f => f.type.startsWith('image/'));
          if (imageFiles.length > 0) {
            const imageFile = imageFiles[0]; // Use the first image file
            imageSource = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target.result);
              reader.onerror = (e) => reject(e);
              reader.readAsDataURL(imageFile);
            });
            displayText = imageFile.name;
            console.log('✅ Using uploaded image file:', imageFile.name);
          } else {
            console.log('❌ No image files uploaded');
            const errorMessage = {
              id: Date.now() + 1,
              text: `Please upload an image file to analyze.`,
              sender: 'bot'
            };
            setMessages(prev => [...prev, errorMessage]);
            setIsLoading(false);
            return;
          }
        }
        // If no files uploaded, check for URL input
        else if (input && input.trim() !== '') {
          let cleanInput = input.trim();
          console.log('User provided URL input:', cleanInput);

          if (isValidImageUrl(cleanInput)) {
            imageSource = cleanInput;
            displayText = cleanInput;
            console.log('✅ Using image URL:', cleanInput);
          } else {
            console.log('❌ Invalid image URL:', cleanInput);
            const errorMessage = {
              id: Date.now() + 1,
              text: `Invalid image URL. Please provide a valid image URL or upload an image file.\n\nInput was: "${cleanInput}"`,
              sender: 'bot'
            };
            setMessages(prev => [...prev, errorMessage]);
            setIsLoading(false);
            return;
          }
        } else {
          console.log('❌ No image provided');
          const errorMessage = {
            id: Date.now() + 1,
            text: `Please upload an image file or provide an image URL to analyze.`,
            sender: 'bot'
          };
          setMessages(prev => [...prev, errorMessage]);
          setIsLoading(false);
          return;
        }

        console.log('🎯 Final image source type:', typeof imageSource);
        console.log('🎯 Final image source value:', imageSource);
        console.log('🎯 Using production mode');

        if (!imageSource) {
          console.error('❌ Image source is null or undefined');
          const errorMessage = {
            id: Date.now() + 1,
            text: `Error: No valid image source found. Please try again.`,
            sender: 'bot'
          };
          setMessages(prev => [...prev, errorMessage]);
          setIsLoading(false);
          return;
        }

        // Analyze image using puter.js img2txt (production mode)
        console.log('🚀 Calling puter.ai.img2txt with:', displayText);
        const description = await window.puter.ai.img2txt(imageSource, false);
        console.log('✅ Received description:', description);

        // Create a bot message with the image description
        const sourceType = files.length > 0 ? 'Uploaded File' : 'URL';
        const botMessage = {
          id: Date.now() + 1,
          text: `Image analysis for: "${displayText}"\n\nSource: ${sourceType}\n\nDescription: ${description}`,
          sender: 'bot',
          type: 'text'
        };
        setMessages(prev => [...prev, botMessage]);

      } else {
        // Fallback when puter.js is not available
        console.log('❌ Puter.js not available');
        const botMessage = {
          id: Date.now() + 1,
          text: `Image-to-text analysis not available. Puter.js img2txt API is not loaded.\n\nPuter loaded: ${puterLoaded}\nWindow.puter: ${!!window.puter}\nWindow.puter.ai: ${!!(window.puter && window.puter.ai)}\nWindow.puter.ai.img2txt: ${!!(window.puter && window.puter.ai && window.puter.ai.img2txt)}`,
          sender: 'bot'
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      // Error handling for image-to-text
      console.error('❌ Image analysis error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: `Image analysis failed: ${error.message}\n\nError details: ${JSON.stringify(error, null, 2)}`,
        sender: 'bot'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <Header
        mode={mode}
        setMode={setMode}
        models={models}
        modelsLoading={modelsLoading}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        modelSearchTerm={modelSearchTerm}
        setModelSearchTerm={setModelSearchTerm}
        isModelDropdownOpen={isModelDropdownOpen}
        setIsModelDropdownOpen={setIsModelDropdownOpen}
        modelDropdownRef={modelDropdownRef}
        toggleModelDropdown={toggleModelDropdown}
        getSelectedModelName={getSelectedModelName}
        handleModelSelect={handleModelSelect}
        handleModelSearchChange={handleModelSearchChange}
        filteredModels={filteredModels}
        gpt4freeModels={gpt4freeModels}
        selectedGpt4freeModel={selectedGpt4freeModel}
        setSelectedGpt4freeModel={setSelectedGpt4freeModel}
        gpt4freeModelsLoading={gpt4freeModelsLoading}
      />

      {!puterLoaded && (
        <div className="loading-notice">
          Puter.js is loading, please wait...
        </div>
      )}

      {models.length === 0 && puterLoaded && (
        <div className="error-notice">
          No chat models available. Please check the API response or your network connection.
        </div>
      )}

      <div className="chat-container">
        <MessageList
          messages={messages}
          isLoading={isLoading}
          messagesEndRef={messagesEndRef}
        />

        <MessageInput
          mode={mode}
          inputValue={inputValue}
          setInputValue={setInputValue}
          isLoading={isLoading}
          files={files}
          handleFileSelect={handleFileSelect}
          handleSendMessage={handleSendMessage}
          handleKeyDown={handleKeyDown}
          handleInputChange={handleInputChange}
          textareaRef={textareaRef}
          ttsLanguage={ttsLanguage}
          setTtsLanguage={setTtsLanguage}
          ttsVoice={ttsVoice}
          setTtsVoice={setTtsVoice}
          ttsEngine={ttsEngine}
          setTtsEngine={setTtsEngine}
        />
      </div>
    </div>
  );
}

export default App;